import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getTodaySchedule, getGreeting, getSessionState, saveSessionState, isTestAccount,
} from '../../utils/sessionSchedule';
import { unlockAudio } from '../../utils/elevenlabs';
import {
  CHESS_LESSONS, CHESS_TOTAL_LESSONS, chessDone, getAchievements,
} from '../../utils/progress';
import DashShell from '../../components/layout/DashShell';
import WeekTimetable from '../../components/layout/WeekTimetable';
import './TodaySession.css';

// Student dashboard (route /child/today) — docs/redesign/reference-dashboards.html.
// Students only: this page has NO link, button or route to any parent page.

export default function TodaySession() {
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [isPreview, setIsPreview] = useState(false);
  const [sessionState, setSessionState] = useState(null);

  useEffect(() => {
    const own = sessionStorage.getItem('childSession');
    const preview = sessionStorage.getItem('parentPreview');
    if (!own && !preview) { navigate('/child/login'); return; }
    const c = JSON.parse(own || preview);
    setChild(c);
    setIsPreview(!own);
    setSessionState(getSessionState(c.id));
    document.title = 'My Dashboard · Talent School Africa';
  }, [navigate]);

  if (!child) return null;

  const isTest = isTestAccount(child);
  const schedule = getTodaySchedule();
  const done = chessDone(child);
  const builtLessons = CHESS_LESSONS.length;
  const current = done < builtLessons ? CHESS_LESSONS[done] : null;
  const achievements = getAchievements(child);
  const earned = achievements.filter(a => a.earned).length;
  const pct = Math.round((done / CHESS_TOTAL_LESSONS) * 100);
  const nextBadge = achievements.find(a => !a.earned);

  // Today's session (warm-up → lesson). The timetable in config/siteConfig.js
  // decides which days are live; test accounts may start chess any day.
  const inProgress = sessionState && !sessionState.completed
    && (!isTest || sessionState.selectedSubject === 'chess');
  const doneToday = !isTest && sessionState?.completed === true;
  const canStartToday = isTest || (schedule.live && schedule.subject === 'chess');

  function startChess() {
    unlockAudio();
    if (inProgress) {
      navigate(sessionState.warmupComplete ? '/child/session/main' : '/child/session/warmup');
      return;
    }
    const initial = {
      childId: child.id,
      date: new Date().toISOString().slice(0, 10),
      phase: 'warmup', warmupComplete: false, mainComplete: false, completed: false,
      startedAt: Date.now(),
      ...(isTest ? { testMode: true, selectedSubject: 'chess' } : {}),
    };
    saveSessionState(child.id, initial);
    if (isTest) sessionStorage.setItem('testSubject', 'chess');
    navigate('/child/session/warmup');
  }

  let cta = null;     // { label, onClick } when the student can go
  let note = null;    // { title, text } when they can't
  if (!current) note = { title: 'All lessons done!', text: `You've finished all ${builtLessons} lessons so far. More are coming soon!` };
  else if (doneToday) note = { title: 'Great work today!', text: 'You finished today\'s session. See you tomorrow!' };
  else if (!canStartToday) note = { title: schedule.soonTitle, text: schedule.soonText };
  else cta = { label: inProgress ? 'Continue lesson →' : 'Start lesson →', onClick: startChess };

  const message = !current
    ? 'You have completed every lesson available right now.'
    : cta
      ? `Ms. Momo is ready for Lesson ${current.n}: ${current.name}.`
      : `Next chess lesson: Lesson ${current.n}, ${current.name}.`;
  const streak = child.streak || 0;

  function logout() {
    sessionStorage.removeItem('childSession');
    sessionStorage.removeItem('testSubject');
    navigate('/child/login');
  }

  const nav = [
    { icon: '🏠', label: 'My Dashboard', active: true, onClick: () => window.scrollTo(0, 0) },
    ...(cta ? [{ icon: '♟️', label: inProgress ? 'Continue lesson' : 'Start lesson', onClick: cta.onClick }] : []),
    { icon: '🏆', label: 'My Badges', onClick: () => document.getElementById('my-badges')?.scrollIntoView({ behavior: 'smooth' }) },
    ...(isPreview ? [] : [{ icon: '🚪', label: 'Log out', onClick: logout }]),
  ];

  return (
    <DashShell
      user={{ initial: (child.name || '?')[0], name: child.name, color: child.avatarColor || 'var(--green)', sub: `Student · ${child.studentId}` }}
      navLabel="Learning"
      nav={nav}
      foot={<><b>Tip from Ms. Momo 🦉</b>Watch my board first, then copy me on yours!</>}
      title="My Dashboard"
      chips={<><span className="chip">🔥 {streak}-day streak</span><span className="chip">⭐ {(child.totalXP || 0).toLocaleString()} points</span></>}
      banner={isPreview && <div className="dash-banner">👀 You are previewing {child.name}'s dashboard</div>}
    >
      <div className="welcome">
        <img className="welcome-owl" src="/images/momo-owl.png" alt="Ms. Momo" />
        <div className="welcome-txt">
          <h2>{getGreeting()}, {child.name}! 👋</h2>
          <p>{message}{streak > 1 ? ` You're ${streak} days in a row!` : ''}</p>
        </div>
        {cta
          ? <button type="button" className="btn btn-green" onClick={cta.onClick}>{cta.label}</button>
          : <div className={`welcome-note wn-${schedule.subject}`} role="status"><b>{note.title}</b>{note.text}</div>}
      </div>

      <div className="stats">
        <div className="stat" style={{ background: 'var(--t-peach)' }}><div className="stat-ic">🔥</div><div><b>{streak}</b><span>Day streak</span></div></div>
        <div className="stat" style={{ background: 'var(--t-green)' }}><div className="stat-ic">📚</div><div><b>{done} / {CHESS_TOTAL_LESSONS}</b><span>Lessons done</span></div></div>
        <div className="stat" style={{ background: 'var(--yellow-bg)' }}><div className="stat-ic">⭐</div><div><b>{(child.totalXP || 0).toLocaleString()}</b><span>Points</span></div></div>
        <div className="stat" style={{ background: 'var(--t-lav)' }}><div className="stat-ic">🏅</div><div><b>{earned}</b><span>Badges</span></div></div>
      </div>

      <div className="two">
        <div className="card">
          <div className="card-h"><h2>♟ My Chess Path</h2><span className="muted">Lessons 1–{builtLessons}</span></div>
          <div className="prog-l"><span>{current ? `Lesson ${current.n} of ${CHESS_TOTAL_LESSONS}` : `${done} of ${CHESS_TOTAL_LESSONS} done`}</span><span>{pct}% of the course</span></div>
          <div className="prog" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Chess course progress"><i style={{ width: `${pct}%` }} /></div>
          <div className="path">
            {CHESS_LESSONS.map((l, i) => {
              const state = i < done ? 'done' : i === done ? 'now' : 'lock';
              return (
                <div key={l.n} className={`lesson ${state}`}>
                  <div className="n">{state === 'done' ? '✓' : l.n}</div>
                  <div className="lesson-txt">
                    <b>Lesson {l.n} · {l.name}</b>
                    <span>{state === 'lock' ? `Unlocks after Lesson ${l.n - 1}` : state === 'now' && inProgress ? `${l.detail} · ${sessionState.warmupComplete ? 'warm-up done' : 'started today'}` : l.detail}</span>
                  </div>
                  <span className="st">{state === 'done' ? 'Done ✓' : state === 'now' ? (inProgress ? 'In progress' : 'Up next') : '🔒 Locked'}</span>
                  {state === 'now' && cta && <button type="button" className="btn btn-green" onClick={cta.onClick}>Go</button>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="dash-col">
          <div className="card" id="my-badges">
            <div className="card-h"><h2>🏆 My Badges</h2><span className="muted">{earned} of {achievements.length}</span></div>
            <div className="badges">
              {achievements.map(a => (
                <div key={a.name} className={`badge ${a.earned ? '' : 'off'}`} style={{ background: a.tint }}>
                  <div className="bi">{a.icon}</div><b>{a.name}</b><span>{a.desc}</span>
                  <span className="sr-only">{a.earned ? 'Earned' : 'Not earned yet'}</span>
                </div>
              ))}
            </div>
            <p className="next-badge">{nextBadge ? <>Next badge: <b>{nextBadge.name}</b> · {nextBadge.desc}</> : 'You have every badge so far! 🎉'}</p>
          </div>
          <div className="card">
            <div className="card-h"><h2>📅 My week</h2><span className="muted">Today: {schedule.label}</span></div>
            <WeekTimetable today={schedule.dayNum} />
          </div>
        </div>
      </div>
    </DashShell>
  );
}
